// Zod-схемы для форм. Каждая схема 1-в-1 со server-side rules
// (Sprint 1 §4.x для auth, Sprint 2 §3.1 для survey). Расхождение = баг.
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §4.

import { z } from 'zod';

// ─── общие блоки ──────────────────────────────────────────────────────────

const PasswordSchema = z
  .string()
  .min(8, 'Минимум 8 символов')
  .regex(/[a-zA-Z]/, 'Хотя бы одна буква')
  .regex(/[0-9]/, 'Хотя бы одна цифра');

const EmailSchema = z
  .string()
  .email('Неверный формат email')
  .max(254, 'Email слишком длинный');

const StrictBoolean = z.boolean({
  invalid_type_error: 'Значение должно быть true или false',
});

// ─── auth ─────────────────────────────────────────────────────────────────

// HEAL-50: повтор пароля убран из регистрации. Он был чисто фронтовым — бэкенд
// (RegisterRequest) его никогда не принимал и не проверял, так что удаление поля
// ничего на сервере не меняет. Страховка от опечатки остаётся: пароль можно
// восстановить кодом на почту, а лишнее поле удлиняло форму и вызывало вопросы.
// Сброс пароля (ResetPasswordSchema ниже) повтор СОХРАНЯЕТ — там опечатка стоит
// дороже: пользователь уже потерял доступ и второй раз восстанавливать нечем.
export const SignupSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  first_name: z.string().trim().min(1, 'Имя обязательно').max(100),
  last_name: z.string().trim().min(1, 'Фамилия обязательна').max(100),
});
export type SignupForm = z.infer<typeof SignupSchema>;

// Signin password = .min(1) намеренно: backend LoginController требует только
// required|string. Не лочим legacy-пароли при будущем bumping'е policy.
export const SigninSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Введите пароль'),
});
export type SigninForm = z.infer<typeof SigninSchema>;

export const ForgotPasswordSchema = z.object({
  email: EmailSchema,
});
export type ForgotPasswordForm = z.infer<typeof ForgotPasswordSchema>;

export const ResetPasswordSchema = z
  .object({
    token: z.string().min(1),
    email: EmailSchema,
    password: PasswordSchema,
    password_confirmation: z.string(),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: 'Пароли не совпадают',
    path: ['password_confirmation'],
  });
export type ResetPasswordForm = z.infer<typeof ResetPasswordSchema>;

// ─── consent ──────────────────────────────────────────────────────────────

export const ConsentSchema = z.object({
  pd_medical: StrictBoolean.refine((v) => v === true, 'Согласие обязательно'),
  cross_border_openai: StrictBoolean.refine((v) => v === true, 'Согласие обязательно'),
  tos_privacy: StrictBoolean.refine((v) => v === true, 'Согласие обязательно'),
});
export type ConsentForm = z.infer<typeof ConsentSchema>;

// ─── survey ───────────────────────────────────────────────────────────────

const Sex = z.enum(['male', 'female', 'other'], {
  required_error: 'Выберите пол',
  invalid_type_error: 'Выберите пол',
});
const ActivityLevel = z.enum(['sedentary', 'light', 'moderate', 'high'], {
  required_error: 'Выберите уровень активности',
  invalid_type_error: 'Выберите уровень активности',
});
const StressLevel = z.enum(['low', 'medium', 'high'], {
  required_error: 'Выберите уровень стресса',
  invalid_type_error: 'Выберите уровень стресса',
});
const Smoking = z.enum(['never', 'past', 'current'], {
  required_error: 'Выберите вариант',
  invalid_type_error: 'Выберите вариант',
});
const Alcohol = z.enum(['never', 'rare', 'weekly', 'daily'], {
  required_error: 'Выберите частоту',
  invalid_type_error: 'Выберите частоту',
});

export const Step1BasicSchema = z.object({
  sex: Sex,
  // Round-trip check ловит Feb 30, Apr 31 и т.п. — JS Date нормализует их без ошибки.
  date_of_birth: z
    .string({
      required_error: 'Введите дату рождения',
      invalid_type_error: 'Введите дату рождения',
    })
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Введите дату полностью в формате ДД.ММ.ГГГГ')
    .refine((s) => {
      const d = new Date(s + 'T00:00:00Z');
      if (isNaN(d.getTime())) return false;
      const roundTrip = d.toISOString().slice(0, 10);
      if (roundTrip !== s) return false;
      const min = new Date('1900-01-01T00:00:00Z');
      const max = new Date();
      return d >= min && d <= max;
    }, 'Такой даты не существует или она вне допустимого диапазона'),
  height_cm: z
    .number({
      required_error: 'Введите рост',
      invalid_type_error: 'Введите число',
    })
    .int('Целое число')
    .min(80, 'Минимум 80 см')
    .max(250, 'Максимум 250 см'),
  weight_kg: z
    .number({
      required_error: 'Введите вес',
      invalid_type_error: 'Введите число',
    })
    .multipleOf(0.01, 'Укажите с точностью до 0,01')
    .min(30, 'Минимум 30 кг')
    .max(300, 'Максимум 300 кг'),
});

export const Step2LifestyleSchema = z.object({
  activity_level: ActivityLevel,
  sleep_hours_avg: z
    .number({
      required_error: 'Введите часы сна',
      invalid_type_error: 'Введите число',
    })
    .multipleOf(0.1, 'Укажите с точностью до 0,1')
    .min(0, 'Минимум 0 часов')
    .max(24, 'Максимум 24 часа'),
  stress_level: StressLevel,
  smoking: Smoking,
  alcohol: Alcohol,
  free_text: z.string().max(500).nullable().optional(),
});

export const Step3SymptomsSchema = z.object({
  symptoms: z.array(z.string().max(100)).max(50).default([]),
  blood_pressure_issue: z.boolean(),
  sleep_issue: z.boolean(),
  energy_issue: z.boolean(),
  weight_issue: z.boolean(),
  free_text: z.string().max(500).nullable().optional(),
});

export const Step4HistorySchema = z.object({
  chronic_diagnoses: z.array(z.string().max(200)).max(30).default([]),
  family_history: z.array(z.string().max(200)).max(30).default([]),
  surgeries: z.string().max(1000).nullable().optional(),
  free_text: z.string().max(500).nullable().optional(),
});

// .default(null) вместо .optional() — Zod output ВСЕГДА содержит ключ (string | null).
// Без этого `Step5MedicationsSchema.parse({})` возвращал бы `{}`, и фронт слал бы `{step:{}}` —
// бэк падал в 500 / молча терял данные (prod-инцидент 25.05.2026, v0.99.2 hotfix).
const optionalTrimmedText = (max: number) =>
  z.preprocess((value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }, z.string().max(max).nullable().default(null));

export const Step5MedicationsSchema = z.object({
  medications_text: optionalTrimmedText(2000),
  allergies_text: optionalTrimmedText(1000),
});

export type Step1Form = z.infer<typeof Step1BasicSchema>;
export type Step2Form = z.infer<typeof Step2LifestyleSchema>;
export type Step3Form = z.infer<typeof Step3SymptomsSchema>;
export type Step4Form = z.infer<typeof Step4HistorySchema>;
export type Step5Form = z.infer<typeof Step5MedicationsSchema>;

// ─── uploads (Sprint 4) ───────────────────────────────────────────────────
// Source of truth: docs/specs/sprint-4/stage-1-contracts.md §6.

export const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Введите дату полностью в формате ДД.ММ.ГГГГ')
  .refine((s) => {
    const d = new Date(s + 'T00:00:00Z');
    if (isNaN(d.getTime())) return false;
    if (d.toISOString().slice(0, 10) !== s) return false;
    const min = new Date('1900-01-01T00:00:00Z');
    const max = new Date();
    max.setUTCHours(23, 59, 59, 999);
    return d >= min && d <= max;
  }, 'Такой даты не существует или она вне допустимого диапазона');

export const ConfirmUploadSchema = z.object({
  measured_at: DateOnlySchema.optional(),
});
export type ConfirmUploadForm = z.infer<typeof ConfirmUploadSchema>;

export const ReportUploadSchema = z.object({
  comment: z.string().max(500, 'Максимум 500 символов').trim().nullable().optional(),
});
export type ReportUploadForm = z.infer<typeof ReportUploadSchema>;

export const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'] as const;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const ClientFileSchema = z.object({
  size: z.number().int().min(1, 'Файл пустой').max(MAX_FILE_SIZE_BYTES, 'Файл больше 10 МБ'),
  type: z.enum(ALLOWED_MIME, {
    errorMap: () => ({ message: 'Поддерживаем только PDF, JPEG, PNG' }),
  }),
});
